import { useState } from 'react';
import type { ReactNode } from 'react';
import { TenantStatus } from '@yourstore/domain';
import { useApi, useAction, useRead } from './context';
import { Confirm, Empty, Failure, Loading, Section, StatusPill, When } from './bits';
import { TenantModules } from './TenantModules';
import { TenantKeys } from './TenantKeys';
import { TenantWebhooks } from './TenantWebhooks';
import { TenantDomains } from './TenantDomains';
import { TenantBranding } from './TenantBranding';
import { STATUS_CONSEQUENCE, STATUS_MEANING } from '../vocabulary';

/**
 * One tenant, on one screen — because `admin-api` assembles it in one
 * transaction, so the six lists on it are one moment rather than six.
 *
 * The page is ordered the way an onboarding goes and the way an incident goes,
 * which turn out to be the same order: what this business *is* (status and
 * lifecycle), what it has bought (modules), who can reach it (keys), where its
 * own guests reach it (domains, §6.4.2), where its events go (webhooks and the
 * delivery log), and what has been done to it (audit). Anything about its
 * guests, its bookings or its money is absent, and absent structurally (§6.3).
 */
export function TenantDetail({
  tenantId,
  onBack,
  onAudit,
  onJobs,
}: {
  tenantId: string;
  onBack: () => void;
  onAudit: (tenantId: string) => void;
  onJobs: (tenantId: string) => void;
}): ReactNode {
  const api = useApi();
  const detail = useRead(() => api.tenant(tenantId), [tenantId]);

  return (
    <div className="view">
      <header className="view__head">
        <div>
          <button type="button" className="linkish" onClick={onBack}>
            ← Fleet
          </button>
          <h2>{tenantId}</h2>
        </div>
        <div className="view__actions">
          <button type="button" className="button" onClick={() => onAudit(tenantId)}>
            Audit for this tenant
          </button>
          <button type="button" className="button" onClick={() => onJobs(tenantId)}>
            Jobs
          </button>
          <button
            type="button"
            className="button"
            onClick={detail.reload}
            disabled={detail.loading}
          >
            Refresh
          </button>
        </div>
      </header>

      {detail.failure !== null ? <Failure failure={detail.failure} /> : null}
      {detail.value === null ? (
        detail.loading ? (
          <Loading what="the tenant" />
        ) : null
      ) : (
        <>
          <Lifecycle
            tenantId={tenantId}
            name={detail.value.tenant.name}
            timezone={detail.value.tenant.timezone}
            currency={detail.value.tenant.currency}
            plan={detail.value.tenant.plan}
            status={detail.value.tenant.status}
            authority={detail.value.tenant.authority}
            catalogVersion={detail.value.config?.catalog_version ?? null}
            onChanged={detail.reload}
          />

          <TenantModules
            tenantId={tenantId}
            modules={detail.value.modules}
            onChanged={detail.reload}
          />

          <TenantKeys
            tenantId={tenantId}
            keys={detail.value.keys}
            enabledModules={detail.value.modules
              .filter((m) => m.status !== 'off')
              .map((m) => m.module)}
            onChanged={detail.reload}
          />

          {/*
           * Domains sit between "who can reach it" and "where its events go",
           * because that is where they sit in an onboarding: the keys are how
           * the partner reaches the tenant, the domain is how the tenant's own
           * guests do. It fetches its own list rather than riding the tenant
           * payload — the same shape the delivery log uses, so that adding a
           * hostname reloads one card and not the page.
           */}
          <TenantDomains tenantId={tenantId} />

          {/*
           * Branding sits directly under Domains, because they are two of the
           * three rows §6.4.2 says a tenant's website is: the domain decides
           * *where* the site answers, this decides *what it looks like*. It
           * fetches its own payload for the same reason the domains card does —
           * the theme contract vocabulary rides that read and has no business on
           * every tenant-page load — and reloads one card on publish.
           */}
          <TenantBranding tenantId={tenantId} />

          <TenantWebhooks
            tenantId={tenantId}
            endpoints={detail.value.webhook_endpoints}
            onChanged={detail.reload}
          />

          <Section
            title="Recent activity"
            note="The last twenty audited actions on this tenant. The full trail, across tenants, is the Audit tab."
          >
            {detail.value.recent_audit.length === 0 ? (
              <Empty>Nothing yet.</Empty>
            ) : (
              <table className="table table--dense">
                <thead>
                  <tr>
                    <th>When</th>
                    <th>Actor</th>
                    <th>Action</th>
                    <th>Target</th>
                  </tr>
                </thead>
                <tbody>
                  {detail.value.recent_audit.map((a) => (
                    <tr key={a.audit_id}>
                      <td>
                        <When iso={a.at} />
                      </td>
                      <td>{a.actor}</td>
                      <td>
                        <code>{a.action}</code>
                      </td>
                      <td className="muted">{a.target}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </Section>
        </>
      )}
    </div>
  );
}

/**
 * Status, and the moves out of it.
 *
 * Every move is confirmed, and the confirm quotes the consequence rather than
 * asking whether the operator is sure. Flipping to `live` is **the cutover
 * step**: from that moment `authority: "live"` is reported to the agent, which
 * will promise a guest a time on the strength of it. That is not a sentence a
 * dialog should paraphrase, so `vocabulary.ts` holds it and this renders it.
 *
 * The no-op is not offered: `admin-api` answers `409` on a status flip that
 * changes nothing — deliberately, because a no-op is either a second operator
 * having already done it or a console pointed at the wrong environment. The
 * current status is therefore shown as a pill and not as a button, so the
 * refusal is a backstop rather than the normal path.
 */
function Lifecycle({
  tenantId,
  name,
  timezone,
  currency,
  plan,
  status,
  authority,
  catalogVersion,
  onChanged,
}: {
  tenantId: string;
  name: string;
  timezone: string;
  currency: string;
  plan: string;
  status: TenantStatus;
  authority: 'live' | 'approximate';
  catalogVersion: number | null;
  onChanged: () => void;
}): ReactNode {
  const api = useApi();
  const action = useAction();
  const [pending, setPending] = useState<TenantStatus | null>(null);
  const [result, setResult] = useState<string | null>(null);

  const confirm = async (): Promise<void> => {
    if (pending === null) return;
    const ok = await action.run(async () => {
      const answer = await api.setStatus(tenantId, pending);
      setResult(
        `${answer.tenant_id}: ${answer.previous_status} → ${answer.status}. ` +
          `Availability now reports authority "${answer.authority}"; every warm instance ` +
          `agrees within ${answer.effective_within_seconds} seconds.`,
      );
    });
    if (ok) {
      setPending(null);
      onChanged();
    }
  };

  return (
    <Section
      title="Lifecycle"
      note={STATUS_MEANING[status] ?? ''}
      actions={<StatusPill status={status} />}
    >
      <dl className="facts">
        <div>
          <dt>Name</dt>
          <dd>{name}</dd>
        </div>
        <div>
          <dt>Timezone</dt>
          <dd>{timezone}</dd>
        </div>
        <div>
          <dt>Currency</dt>
          <dd>{currency}</dd>
        </div>
        <div>
          <dt>Plan</dt>
          <dd>{plan}</dd>
        </div>
        <div>
          <dt>Authority</dt>
          <dd>
            <code>{authority}</code>
          </dd>
        </div>
        <div>
          <dt>Catalog version</dt>
          <dd>{catalogVersion === null ? <span className="muted">—</span> : catalogVersion}</dd>
        </div>
      </dl>

      <div className="row-actions">
        {TenantStatus.options
          .filter((s) => s !== status)
          .map((s) => (
            <button
              key={s}
              type="button"
              className="button"
              onClick={() => {
                action.clear();
                setResult(null);
                setPending(s);
              }}
            >
              Move to {s}
            </button>
          ))}
      </div>

      {result !== null ? <p className="ok">{result}</p> : null}
      {pending === null && action.failure !== null ? <Failure failure={action.failure} /> : null}

      {pending !== null ? (
        <Confirm
          title={`Move ${tenantId} to ${pending}?`}
          consequence={STATUS_CONSEQUENCE[pending] ?? ''}
          confirmLabel={`Move ${tenantId} to ${pending}`}
          busy={action.busy}
          failure={action.failure}
          onConfirm={() => void confirm()}
          onCancel={() => {
            action.clear();
            setPending(null);
          }}
        />
      ) : null}
    </Section>
  );
}
